import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Undo2 } from "lucide-react";
import { toast } from "sonner";

import { SalesShell } from "@/components/crm/SalesShell";
import { EmptyState } from "@/components/crm/parts";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { gbp } from "@/lib/crm/types";
import { useImportBatches, type ImportBatch } from "@/lib/crm/useImports";
import { useOrgMembers } from "@/lib/crm/useOrgMembers";
import { useRollbackImport } from "@/lib/crm/useHubspotImport";
import { usePermissions } from "@/lib/crm/usePermissions";

export const Route = createFileRoute("/_authenticated/sales/admin/import-history")({
  head: () => ({
    meta: [
      { title: "Import history & rollback — MyPriorities" },
      {
        name: "description",
        content:
          "Review every confirmed CRM import and roll one back safely: records it created are removed and records it changed are restored to their previous values.",
      },
      { property: "og:title", content: "Import history & rollback — MyPriorities" },
      {
        property: "og:description",
        content:
          "Full audit trail of CRM and HubSpot imports, with a one-click rollback that never touches your existing contracts.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: ImportHistoryPage,
});

const when = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : "—";

const STATUS_LABEL: Record<string, string> = {
  pending: "Not confirmed",
  running: "In progress",
  completed: "Complete",
  completed_with_issues: "Complete with issues",
  rolled_back: "Rolled back",
};

function ImportHistoryPage() {
  return (
    <SalesShell
      title="Import history"
      subtitle="Admin only. Every confirmed import, and a safe rollback for any of them."
    >
      {({ orgId, role }) => <Body orgId={orgId} isAdmin={role === "admin"} />}
    </SalesShell>
  );
}

function Body({ orgId, isAdmin }: { orgId: string; isAdmin: boolean }) {
  const { can, ready } = usePermissions();
  const batches = useImportBatches(orgId);
  const members = useOrgMembers(orgId);
  const rollback = useRollbackImport();
  const [target, setTarget] = useState<ImportBatch | null>(null);

  if (ready && (!isAdmin || !can("import_hubspot"))) {
    return (
      <EmptyState
        title="Admins only"
        body="Rolling an import back changes organisation-wide commercial data, so it's limited to admins with import rights."
      />
    );
  }

  const run = async () => {
    if (!target) return;
    try {
      const result = await rollback.mutateAsync({ organisationId: orgId, batchId: target.id });
      toast.success(
        `Rolled back ${result.softDeleted.toLocaleString()} imported records and restored ${result.restored.toLocaleString()} updated ones`,
      );
      setTarget(null);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "That rollback could not be completed");
    }
  };

  const rows = batches.data ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="font-display">Imports and rollbacks</CardTitle>
        <CardDescription>
          Rolling back removes only the records that import created and restores anything it
          changed. Existing contracts, tasks and business cases are never touched.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {batches.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading history…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No imports yet.</p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>File</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead className="text-right">Created</TableHead>
                  <TableHead className="text-right">Updated</TableHead>
                  <TableHead className="text-right">Value</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((batch) => {
                  const rolledBack = Boolean(batch.rolled_back_at);
                  return (
                    <TableRow key={batch.id}>
                      <TableCell className="text-xs">
                        {when(batch.confirmed_at ?? batch.uploaded_at)}
                      </TableCell>
                      <TableCell className="font-medium">
                        {batch.filename}
                        {batch.kind ? (
                          <span className="text-xs text-muted-foreground"> · {batch.kind}</span>
                        ) : null}
                      </TableCell>
                      <TableCell className="text-xs">
                        {members.ownerLabel(batch.created_by)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {batch.records_created}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {batch.records_updated ?? 0}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {gbp(batch.total_acv)}
                      </TableCell>
                      <TableCell>
                        <Badge variant={rolledBack ? "outline" : "secondary"}>
                          {STATUS_LABEL[batch.status] ?? batch.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={rolledBack || batch.records_created === 0}
                          onClick={() => setTarget(batch)}
                        >
                          <Undo2 className="mr-1.5 h-3.5 w-3.5" />
                          {rolledBack ? "Rolled back" : "Roll back"}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      <Dialog open={Boolean(target)} onOpenChange={(open) => (open ? null : setTarget(null))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Roll back this import?</DialogTitle>
            <DialogDescription>
              {target
                ? `${target.filename} — ${target.records_created.toLocaleString()} records created and ${(target.records_updated ?? 0).toLocaleString()} updated on ${when(target.confirmed_at ?? target.uploaded_at)}.`
                : ""}
            </DialogDescription>
          </DialogHeader>
          <ul className="grid gap-1 text-sm">
            <li>• Records this import created are removed from the CRM.</li>
            <li>• Records it changed go back to the values they had before.</li>
            <li>• Multi-year groupings it created are removed.</li>
            <li>• Nothing else in your CRM is affected, and the audit trail is kept.</li>
          </ul>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setTarget(null)}>
              Cancel
            </Button>
            <Button onClick={() => void run()} disabled={rollback.isPending}>
              {rollback.isPending ? "Rolling back…" : "Roll back import"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
